import { createContext, useContext, useEffect, useMemo } from 'react';

import { useMutation } from '@tanstack/react-query';

import { SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import { useProfile } from 'context/profile';
import GroupType from 'models/GroupType';
import Profile from 'models/Profile';
import { upsertInsightsFilter } from 'services/api/preferences';
import { useReferralsInsightsActions, useReferralsInsightsStore } from 'stores/referralsInsightsStore';

import ReferralsPage from './ReferralsPage';

type ReferralsInsightsContextType = {
  profile: Profile;
  sendingGroupType: GroupType | undefined;
  receivingGroupTypes: GroupType[];
  onTabChange: (groupType: GroupType) => void;
};

export const ReferralsInsightsContext = createContext<ReferralsInsightsContextType>({
  profile: {} as Profile,
  sendingGroupType: undefined,
  receivingGroupTypes: [],
  onTabChange: () => {},
});

export const useReferralsInsightsContext = () => useContext(ReferralsInsightsContext);

const ReferralsContainer = () => {
  const { profile } = useProfile();
  const selectedReceivingGroupType = useReferralsInsightsStore((state) => state.selectedReceivingGroupType);
  const { setSelectedReceivingGroupType, setSendingGroupTypeId, resetFilters } = useReferralsInsightsActions();

  const { mutate: savePreference } = useMutation({ mutationFn: upsertInsightsFilter });

  const receivingGroupTypes = useMemo(() => {
    const managed = profile?.actingClient?.managedProviderTypes ?? [];
    const providedIds = new Set(profile?.actingClient?.providedProviderTypes?.map((gt) => gt.id) ?? []);
    const isProvided = (gt: GroupType) => providedIds.has(gt.id);

    // Managed-only group types first (these are the levels of care the SNF cares most about
    // for referral performance), then provided; within each group, sort by creation date
    return [...managed].sort((a, b) => {
      const providedDiff = Number(isProvided(a)) - Number(isProvided(b));
      return providedDiff || (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
    });
  }, [profile]);

  const handleTabChange = (groupType: GroupType) => {
    if (groupType.apiName === selectedReceivingGroupType?.apiName) return;

    setSelectedReceivingGroupType(groupType);
    savePreference({
      clientId: profile.actingClient.id,
      value: {
        ...profile.insightsPreference?.value,
        referralsProviderType: groupType.apiName,
      },
    });
  };

  const sendingGroupType = useMemo(
    () => profile.actingClient.providedProviderTypes.find((gt) => gt.apiName === SKILLED_NURSING_FACILITY),
    [profile]
  );

  useEffect(() => {
    setSendingGroupTypeId(sendingGroupType?.id);
  }, [sendingGroupType, setSendingGroupTypeId]);

  const receivingGroupType = useMemo(() => {
    if (receivingGroupTypes.length === 0) return undefined;

    const savedApiName = profile.insightsPreference?.value?.referralsProviderType;

    return (
      receivingGroupTypes.find((gt) => gt.apiName === selectedReceivingGroupType?.apiName) ||
      (savedApiName ? receivingGroupTypes.find((gt) => gt.apiName === savedApiName) : undefined) ||
      receivingGroupTypes[0]
    );
  }, [receivingGroupTypes, selectedReceivingGroupType?.apiName, profile]);

  useEffect(() => {
    if (receivingGroupType) {
      setSelectedReceivingGroupType(receivingGroupType);
    }
  }, [receivingGroupType, setSelectedReceivingGroupType]);

  useEffect(() => {
    resetFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.actingClient.id]);

  return (
    <ReferralsInsightsContext value={{ profile, sendingGroupType, receivingGroupTypes, onTabChange: handleTabChange }}>
      {sendingGroupType && selectedReceivingGroupType && <ReferralsPage />}
    </ReferralsInsightsContext>
  );
};

export default ReferralsContainer;
